import { useEffect, useState } from "react";
import api from "../lib/api";
import { formatIDR } from "../lib/format";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { TrendingUp, TrendingDown, AlertTriangle, Landmark, ShoppingCart, Receipt, Package, ShieldAlert, FileWarning } from "lucide-react";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { useAuth } from "../context/AuthContext";

const KPI = ({ label, value, icon: Icon, tone = "navy", testId }) => (
  <Card className="card-elevated border-slate-200" data-testid={testId}>
    <CardContent className="p-5">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">{label}</div>
          <div className="mt-2 font-display text-2xl font-extrabold text-slate-900 tracking-tight">{value}</div>
        </div>
        <div className={`h-11 w-11 rounded-xl flex items-center justify-center ${tone === "gold" ? "bg-yellow-50 text-yellow-700" : tone === "green" ? "bg-emerald-50 text-emerald-600" : tone === "red" ? "bg-red-50 text-red-600" : "bg-navy-900 text-yellow-400"}`}>
          <Icon size={20} />
        </div>
      </div>
    </CardContent>
  </Card>
);

export default function Dashboard() {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/dashboard/stats").then((r) => setStats(r.data)).catch(() => {});
  }, []);

  const chartData = (stats?.cash_flow || []).slice(0, 30).reverse().map((c) => ({
    date: new Date(c.date).toLocaleDateString("id-ID", { day: "2-digit", month: "short" }),
    Masuk: c.type === "in" ? c.amount : 0,
    Keluar: c.type === "out" ? c.amount : 0,
  }));

  return (
    <div className="space-y-6" data-testid="dashboard-page">
      <div>
        <div className="text-[11px] font-medium uppercase tracking-widest text-slate-500">Selamat datang, {user.full_name}</div>
        <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-slate-900 tracking-tight">Dashboard Eksekutif</h1>
        <p className="text-sm text-slate-500 mt-1">Ringkasan real-time performa perdagangan Anda.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testId="kpi-total-sales" label="Total Penjualan" value={formatIDR(stats?.total_sales || 0)} icon={TrendingUp} tone="green" />
        <KPI testId="kpi-total-purchase" label="Total Pembelian" value={formatIDR(stats?.total_purchase || 0)} icon={ShoppingCart} tone="navy" />
        <KPI testId="kpi-ar" label="Piutang (AR)" value={formatIDR(stats?.outstanding_ar || 0)} icon={Receipt} tone="gold" />
        <KPI testId="kpi-ap" label="Utang (AP)" value={formatIDR(stats?.outstanding_ap || 0)} icon={Landmark} tone="red" />
      </div>

      {stats?.pending_po_count > 0 && user.role === "admin" && (
        <Card className="border-yellow-300 bg-yellow-50 card-elevated" data-testid="pending-po-widget">
          <CardContent className="p-5 flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-yellow-100 flex items-center justify-center flex-shrink-0">
              <ShieldAlert className="text-yellow-700" size={24} />
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-medium uppercase tracking-widest text-yellow-800">Perlu Perhatian Anda</div>
              <div className="font-display text-xl font-bold text-yellow-900 mt-1">{stats.pending_po_count} PO Menunggu Approval</div>
              <div className="mt-3 space-y-1">
                {(stats.pending_pos || []).map((p) => (
                  <div key={p.id} className="flex justify-between text-sm">
                    <span className="text-slate-700">{p.po_number} · {p.supplier_name}</span>
                    <span className="font-mono font-semibold text-navy-900">{formatIDR(p.total)}</span>
                  </div>
                ))}
              </div>
              <a href="/purchasing" className="inline-block mt-3 text-sm font-semibold text-navy-900 hover:text-yellow-700 underline">Review sekarang →</a>
            </div>
          </CardContent>
        </Card>
      )}

      {stats?.expiring_contracts_count > 0 && user.role === "admin" && (
        <Card className="border-orange-300 bg-orange-50 card-elevated" data-testid="expiring-contracts-widget">
          <CardContent className="p-5 flex items-start gap-4">
            <div className="h-12 w-12 rounded-xl bg-orange-100 flex items-center justify-center flex-shrink-0">
              <FileWarning className="text-orange-700" size={24} />
            </div>
            <div className="flex-1">
              <div className="text-[11px] font-medium uppercase tracking-widest text-orange-800">Kontrak Akan Berakhir</div>
              <div className="font-display text-xl font-bold text-orange-900 mt-1">{stats.expiring_contracts_count} Kontrak habis ≤ 30 hari</div>
              <div className="mt-3 space-y-1">
                {(stats.expiring_contracts || []).map((c) => (
                  <div key={c.id} className="flex justify-between text-sm" data-testid={`expiring-contract-${c.contract_number}`}>
                    <span className="text-slate-700">{c.contract_number} · {c.employee_name} <span className="text-xs text-slate-500">({c.contract_type})</span></span>
                    <span className={`font-mono font-semibold ${c.days_left <= 7 ? "text-red-700" : "text-orange-800"}`}>
                      {c.end_date} · {c.days_left} hari lagi
                    </span>
                  </div>
                ))}
              </div>
              <a href="/personalia" className="inline-block mt-3 text-sm font-semibold text-orange-900 hover:text-navy-900 underline">Kelola kontrak →</a>
            </div>
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card className="lg:col-span-2 card-elevated border-slate-200">
          <CardHeader>
            <CardTitle className="font-display flex items-center justify-between">
              <span>Arus Kas</span>
              <Badge variant="outline" className="font-normal">30 hari terakhir</Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-6">
            <div className="h-64" data-testid="cashflow-chart">
              {chartData.length === 0 ? (
                <div className="h-full flex items-center justify-center text-slate-400 text-sm">Belum ada arus kas tercatat</div>
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={chartData}>
                    <defs>
                      <linearGradient id="gIn" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#10B981" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#10B981" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="gOut" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#EF4444" stopOpacity={0.4} />
                        <stop offset="95%" stopColor="#EF4444" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                    <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#64748b" }} />
                    <YAxis tick={{ fontSize: 11, fill: "#64748b" }} tickFormatter={(v) => new Intl.NumberFormat("id-ID", { notation: "compact" }).format(v)} />
                    <Tooltip formatter={(v) => formatIDR(v)} />
                    <Area type="monotone" dataKey="Masuk" stroke="#10B981" fill="url(#gIn)" strokeWidth={2} />
                    <Area type="monotone" dataKey="Keluar" stroke="#EF4444" fill="url(#gOut)" strokeWidth={2} />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="card-elevated border-slate-200">
          <CardHeader>
            <CardTitle className="font-display flex items-center gap-2">
              <AlertTriangle size={18} className="text-yellow-600" /> Stok Menipis
            </CardTitle>
          </CardHeader>
          <CardContent>
            {(stats?.low_stock_items || []).length === 0 ? (
              <div className="text-sm text-slate-400 py-8 text-center">Semua stok aman ✓</div>
            ) : (
              <ul className="space-y-3" data-testid="low-stock-list">
                {stats.low_stock_items.map((p) => (
                  <li key={p.id} className="flex items-start justify-between gap-3">
                    <div className="min-w-0">
                      <div className="text-sm font-semibold text-slate-800 truncate">{p.name}</div>
                      <div className="text-[11px] font-mono text-slate-500">{p.sku}</div>
                    </div>
                    <div className="text-right">
                      <div className="font-mono font-bold text-red-600">{p.stock}</div>
                      <div className="text-[10px] text-slate-400">min {p.min_stock}</div>
                    </div>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KPI testId="kpi-invoices" label="Total Invoice" value={stats?.invoice_count || 0} icon={Receipt} tone="navy" />
        <KPI testId="kpi-po" label="Total PO Diterima" value={stats?.po_count || 0} icon={Package} tone="navy" />
        <KPI testId="kpi-low-stock" label="Item Stok Menipis" value={stats?.low_stock_count || 0} icon={AlertTriangle} tone="red" />
        <KPI testId="kpi-ppn" label="PPN Berlaku" value="12%" icon={TrendingDown} tone="gold" />
      </div>
    </div>
  );
}
